import { type LocationResponse } from "@swivu/client-api";
import {
  cn,
  DrawerOrDialog,
  LOUD_CHIP,
  META_LABEL,
  TabsSegmented
} from "@swivu/shared-ui";
import {
  BuildingOffice2Icon,
  CheckBadgeIcon,
  ChevronRightIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
  QuestionMarkCircleIcon,
  TruckIcon
} from "@heroicons/react/24/outline";
import { useState } from "react";

const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;

type Heroicon = React.FC<React.SVGProps<SVGSVGElement>>;

const READINESS: Record<
  NonNullable<LocationResponse["readiness_state"]>,
  { label: string; Icon: Heroicon; tile: string }
> = {
  field_ready: {
    label: "Field Ready",
    Icon: CheckBadgeIcon,
    tile: "bg-tile-success"
  },
  partial: {
    label: "Partial Info",
    Icon: ExclamationTriangleIcon,
    tile: "bg-tile-warn"
  },
  thin: {
    label: "Limited Info",
    Icon: QuestionMarkCircleIcon,
    tile: "bg-tile-muted"
  }
};

const MAP_PROVIDERS: {
  title: string;
  short: string;
  Icon: Heroicon;
  url: string;
  tile: string;
}[] = [
  {
    title: "Google Maps",
    short: "Maps",
    Icon: MapIcon,
    url: "https://www.google.com/maps?q={lat},{lon}",
    tile: "bg-tile-success"
  },
  {
    title: "Street View",
    short: "Street",
    Icon: MapPinIcon,
    url: "https://www.google.com/maps/@?api=1&map_action=pano&viewpoint={lat},{lon}",
    tile: "bg-tile-alert"
  },
  {
    title: "Google Earth",
    short: "Earth",
    Icon: GlobeAltIcon,
    url: "https://earth.google.com/web/@{lat},{lon},3000a,0d,0y,0h,0t,0r",
    tile: "bg-tile-info"
  },
  {
    title: "EagleView",
    short: "Eagle",
    Icon: MapIcon,
    url: "https://explorer.eagleview.com/index.php?lat={lat}&lon={lon}&angle=Or&zoom=19&orientation=N",
    tile: "bg-tile-schedule"
  },
  {
    title: "OpenStreetMap",
    short: "OSM",
    Icon: MapIcon,
    url: "https://www.openstreetmap.org/?mlat={lat}&mlon={lon}#map=18/{lat}/{lon}",
    tile: "bg-tile-people"
  },
  {
    title: "Weather",
    short: "Weather",
    Icon: CloudIcon,
    url: "https://forecast.weather.gov/MapClick.php?lat={lat}&lon={lon}",
    tile: "bg-tile-calendar"
  },
  {
    title: "USGS Topo",
    short: "Topo",
    Icon: MapIcon,
    url: "https://ngmdb.usgs.gov/topoview/viewer/#14/{lat}/{lon}",
    tile: "bg-tile-energy"
  }
];

type Coords = { lat: number; lon: number };

const getCoords = (location: LocationResponse): Coords | null => {
  if (
    typeof location.latitude !== "number" ||
    typeof location.longitude !== "number"
  ) {
    return null;
  }
  return { lat: location.latitude, lon: location.longitude };
};

const formatAddress = (location: LocationResponse): string =>
  [
    location.street_address,
    location.city,
    location.state_or_region,
    location.postal_code,
    location.country
  ]
    .filter(Boolean)
    .join(", ");

const buildStaticMapUrl = ({
  lat,
  lon,
  width,
  height,
  zoom
}: Coords & {
  width: number;
  height: number;
  zoom: number;
}) => {
  if (!GOOGLE_MAPS_KEY) return null;
  const params = new URLSearchParams({
    center: `${lat},${lon}`,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: "2",
    maptype: "roadmap",
    markers: `color:red|${lat},${lon}`,
    key: GOOGLE_MAPS_KEY
  });
  return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
};

const applyCoords = (template: string, { lat, lon }: Coords): string =>
  template.replaceAll("{lat}", String(lat)).replaceAll("{lon}", String(lon));

const MapHero = ({ coords }: { coords: Coords }) => {
  const url = buildStaticMapUrl({
    ...coords,
    width: 800,
    height: 320,
    zoom: 16
  });
  if (!url) return null;
  return (
    <div className="-mx-5 -mt-5 mb-5 overflow-hidden md:-mx-6 md:-mt-6">
      <div className="relative">
        <img
          src={url}
          alt="Location map"
          className="h-44 w-full object-cover md:h-52"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-mono-950/70 to-transparent"
        />
        <span
          className={cn(
            "absolute bottom-3 left-3 inline-flex h-10 items-center gap-2 rounded-full",
            "bg-mono-950/80 px-3 text-sm font-black text-white shadow-pill backdrop-blur",
            "text-shadow-on-color"
          )}
        >
          <MapPinIcon className="size-4" strokeWidth={2.5} />
          {coords.lat.toFixed(4)}, {coords.lon.toFixed(4)}
        </span>
      </div>
    </div>
  );
};

const ProviderPill = ({
  provider,
  coords
}: {
  provider: (typeof MAP_PROVIDERS)[number];
  coords: Coords;
}) => (
  <a
    href={applyCoords(provider.url, coords)}
    target="_blank"
    rel="noopener noreferrer"
    className={cn(
      "relative inline-flex h-11 items-center gap-2 overflow-hidden whitespace-nowrap",
      "rounded-full px-3 text-sm font-black text-white shadow-pill text-shadow-on-color",
      "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96] motion-reduce:transform-none",
      "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/60",
      provider.tile
    )}
  >
    <span aria-hidden className="top-gloss" />
    <provider.Icon className="relative size-4" strokeWidth={2.5} />
    <span className="relative">{provider.short}</span>
  </a>
);

const FieldChip = ({
  label,
  Icon,
  value
}: {
  label: string;
  Icon: Heroicon;
  value: string;
}) => (
  <div className="rounded-surface-lg bg-white p-4 shadow-raised dark:bg-mono-900">
    <div className="flex items-center gap-2">
      <Icon
        className="size-5 flex-none text-mono-500 dark:text-mono-400"
        strokeWidth={2.5}
      />
      <span className={cn(META_LABEL, "text-xs")}>{label}</span>
    </div>
    <p className="mt-2 break-words text-base font-bold leading-snug text-mono-800 dark:text-mono-100">
      {value}
    </p>
  </div>
);

const NarrativeSection = ({ label, text }: { label: string; text: string }) => (
  <div>
    <span className={META_LABEL}>{label}</span>
    <p className="mt-2 whitespace-pre-line break-words text-base font-bold leading-relaxed text-mono-700 dark:text-mono-300">
      {text}
    </p>
  </div>
);

type TabKey = "work" | "access" | "guidance";
type Tab = {
  key: TabKey;
  label: string;
  content: React.ReactNode;
};

const buildTabs = (location: LocationResponse): Tab[] => {
  const tabs: Tab[] = [];

  const workItems: { label: string; Icon: Heroicon; value: string }[] = [];
  if (location.building_name)
    workItems.push({
      label: "Building",
      Icon: BuildingOffice2Icon,
      value: location.building_name
    });
  if (location.floor_or_level)
    workItems.push({
      label: "Floor / Level",
      Icon: BuildingOffice2Icon,
      value: location.floor_or_level
    });
  if (location.elevation)
    workItems.push({
      label: "Elevation",
      Icon: BuildingOffice2Icon,
      value: location.elevation
    });
  if (location.work_zone_name)
    workItems.push({
      label: "Work Zone",
      Icon: FlagIcon,
      value: location.work_zone_name
    });

  if (workItems.length > 0) {
    tabs.push({
      key: "work",
      label: "Work area",
      content: (
        <div className="grid gap-3 sm:grid-cols-2">
          {workItems.map((item) => (
            <FieldChip key={item.label} {...item} />
          ))}
        </div>
      )
    });
  }

  const accessItems: { label: string; Icon: Heroicon; value: string }[] = [];
  if (location.primary_entry_route)
    accessItems.push({
      label: "Entry Route",
      Icon: TruckIcon,
      value: location.primary_entry_route
    });
  if (location.staging_area)
    accessItems.push({
      label: "Staging Area",
      Icon: FlagIcon,
      value: location.staging_area
    });

  if (accessItems.length > 0) {
    tabs.push({
      key: "access",
      label: "Access",
      content: (
        <div className="grid gap-3 sm:grid-cols-2">
          {accessItems.map((item) => (
            <FieldChip key={item.label} {...item} />
          ))}
        </div>
      )
    });
  }

  const hasGuidance = !!(
    location.start_here_note ||
    location.location_summary ||
    location.workforce_handoff_note
  );
  if (hasGuidance) {
    tabs.push({
      key: "guidance",
      label: "Guidance",
      content: (
        <div className="space-y-5">
          {location.start_here_note && (
            <NarrativeSection
              label="Start Here"
              text={location.start_here_note}
            />
          )}
          {location.location_summary && (
            <NarrativeSection
              label="Location Summary"
              text={location.location_summary}
            />
          )}
          {location.workforce_handoff_note && (
            <NarrativeSection
              label="Workforce Handoff"
              text={location.workforce_handoff_note}
            />
          )}
        </div>
      )
    });
  }

  return tabs;
};

const SectionNav = ({
  tabs,
  active,
  onChange
}: {
  tabs: Tab[];
  active: TabKey;
  onChange: (key: TabKey) => void;
}) => {
  if (tabs.length <= 1) return null;
  return (
    <div
      className={cn(
        "flex w-full items-center gap-1.5 overflow-hidden rounded-full p-1",
        "shadow-sunken bg-white/70 dark:bg-mono-900/70"
      )}
    >
      <TabsSegmented<TabKey>
        onValueChange={(value) => {
          if (value !== null) onChange(value);
        }}
        defaultValue={active}
        className={cn("rounded-full shadow-raised bg-tile-primary")}
        transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
      >
        {tabs.map(({ key, label }) => {
          const isActive = key === active;
          return (
            <button
              key={key}
              data-id={key}
              type="button"
              aria-label={`${label} tab`}
              className={cn(
                "inline-flex min-h-11 flex-1 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-base font-display font-black tracking-tight",
                "transition-colors duration-150",
                "focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
                isActive
                  ? "text-white text-shadow-on-color"
                  : "text-mono-600 dark:text-mono-300 hover:text-mono-900 dark:hover:text-white"
              )}
            >
              {label}
            </button>
          );
        })}
      </TabsSegmented>
    </div>
  );
};

const hasAnyContent = (location: LocationResponse): boolean =>
  !!(
    location.property_name ||
    location.building_name ||
    location.floor_or_level ||
    location.elevation ||
    location.work_zone_name ||
    location.readiness_state ||
    location.primary_entry_route ||
    location.staging_area ||
    location.start_here_note ||
    location.location_summary ||
    location.workforce_handoff_note ||
    location.street_address ||
    location.city ||
    typeof location.latitude === "number" ||
    typeof location.longitude === "number"
  );

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn("rounded-surface-xs bg-mono-200 dark:bg-mono-700", className)}
  />
);

const LocationSkeleton = () => (
  <div className="animate-pulse" role="status" aria-busy aria-live="polite">
    <div className="-mx-5 -mt-5 mb-5 h-44 bg-mono-200 dark:bg-mono-700 md:-mx-6 md:-mt-6 md:h-52" />
    <div className="space-y-6 p-5 md:p-6 md:pt-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonBar className="h-6 w-48 max-w-full" />
          <SkeletonBar className="h-3 w-36 max-w-full rounded-surface-2xs" />
        </div>
        <SkeletonBar className="h-11 w-32 rounded-full" />
      </div>
      <div className="flex flex-wrap gap-2">
        <SkeletonBar className="h-11 w-16 rounded-full" />
        <SkeletonBar className="h-11 w-16 rounded-full" />
        <SkeletonBar className="h-11 w-16 rounded-full" />
      </div>
      <SkeletonBar className="h-12 w-full rounded-full" />
      <div className="grid gap-3 sm:grid-cols-2">
        <SkeletonBar className="h-20 rounded-surface-lg" />
        <SkeletonBar className="h-20 rounded-surface-lg" />
      </div>
    </div>
  </div>
);

const LocationBody = ({ location }: { location: LocationResponse }) => {
  const coords = getCoords(location);
  const address = formatAddress(location);
  const identityLine = [location.property_name, location.building_name]
    .filter(Boolean)
    .join(" • ");
  const readiness = location.readiness_state
    ? READINESS[location.readiness_state]
    : null;
  const tabs = buildTabs(location);
  const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? "work");
  const activeContent = tabs.find((t) => t.key === active)?.content;

  return (
    <>
      {coords && <MapHero coords={coords} />}
      <div className={cn("space-y-5 p-5 md:p-6", coords && "pt-0 md:pt-0")}>
        <DrawerOrDialog.Title asChild>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="break-words font-display text-2xl font-black leading-tight text-mono-900 dark:text-white">
                {identityLine || "Location"}
              </h2>
              {address && (
                <p className="mt-1.5 break-words text-sm font-bold text-mono-600 dark:text-mono-300">
                  {address}
                </p>
              )}
            </div>
            {readiness && (
              <span className={cn(LOUD_CHIP, readiness.tile, "self-start")}>
                <readiness.Icon className="mr-2 size-5" strokeWidth={2.5} />
                {readiness.label}
              </span>
            )}
          </div>
        </DrawerOrDialog.Title>

        {coords && (
          <div className="flex flex-wrap items-center gap-2">
            {MAP_PROVIDERS.map((provider) => (
              <ProviderPill
                key={provider.title}
                provider={provider}
                coords={coords}
              />
            ))}
          </div>
        )}

        {tabs.length > 0 && (
          <>
            <SectionNav tabs={tabs} active={active} onChange={setActive} />
            <div>{activeContent}</div>
          </>
        )}
      </div>
    </>
  );
};

const LocationDetail = ({
  location,
  isLoading
}: {
  location: LocationResponse | undefined;
  isLoading: boolean;
}) => {
  if (!isLoading && !location) return null;
  if (location && !hasAnyContent(location)) return null;

  const coords = location ? getCoords(location) : null;
  const mapUrl = coords
    ? buildStaticMapUrl({ ...coords, width: 800, height: 320, zoom: 16 })
    : null;
  const identityLine = location
    ? [location.property_name, location.building_name]
        .filter(Boolean)
        .join(" • ")
    : "";
  const address = location ? formatAddress(location) : "";
  const readiness = location?.readiness_state
    ? READINESS[location.readiness_state]
    : null;
  const hasMap = !!mapUrl;
  // Full-width banner only when there's a map. Otherwise, compact pill.
  const useBanner = hasMap || isLoading;

  return (
    <DrawerOrDialog.Root drawer={{}} dialog={{}}>
      <DrawerOrDialog.Trigger asChild>
        {useBanner ? (
          <button
            type="button"
            className={cn(
              "relative block w-full self-stretch overflow-hidden text-left",
              "rounded-surface-xl shadow-card",
              "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.99] motion-reduce:transform-none",
              "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
            )}
          >
            <span className="relative block h-40 bg-mono-200 dark:bg-mono-800 md:h-44">
              {mapUrl && (
                <img
                  src={mapUrl}
                  alt=""
                  aria-hidden
                  loading="eager"
                  className="size-full object-cover"
                />
              )}
              {readiness && (
                <span
                  className={cn(
                    LOUD_CHIP,
                    readiness.tile,
                    "absolute right-3 top-3"
                  )}
                >
                  <readiness.Icon className="mr-2 size-5" strokeWidth={2.5} />
                  {readiness.label}
                </span>
              )}
            </span>
            <span className="relative block overflow-hidden bg-tile-primary p-4 text-white md:p-5">
              <span aria-hidden className="top-gloss" />
              <span className="relative flex items-center gap-3">
                <BuildingOffice2Icon
                  className="size-7 flex-none"
                  strokeWidth={2.5}
                />
                <span className="min-w-0 flex-1 text-left text-shadow-on-color">
                  <span className="block truncate font-display text-xl font-black leading-tight md:text-2xl">
                    {identityLine || "Location"}
                  </span>
                  {address && (
                    <span className="mt-0.5 block truncate text-sm font-bold leading-snug text-white/90">
                      {address}
                    </span>
                  )}
                </span>
                <ChevronRightIcon
                  className="size-6 flex-none opacity-80"
                  strokeWidth={2.5}
                />
              </span>
            </span>
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              "relative inline-flex h-12 max-w-full items-center gap-2 self-start overflow-hidden",
              "rounded-full bg-tile-primary px-4 text-base font-black text-white shadow-pill text-shadow-on-color",
              "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.96] motion-reduce:transform-none",
              "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40"
            )}
          >
            <span aria-hidden className="top-gloss" />
            <BuildingOffice2Icon
              className="relative size-5 flex-none"
              strokeWidth={2.5}
            />
            <span className="relative truncate">
              {identityLine || "Location"}
            </span>
            <ChevronRightIcon
              className="relative size-4 flex-none opacity-90"
              strokeWidth={2.5}
            />
          </button>
        )}
      </DrawerOrDialog.Trigger>

      <DrawerOrDialog.Content
        position="top"
        className="bg-mono-100 dark:bg-mono-950 md:max-w-(--breakpoint-md) md:flex md:flex-col"
      >
        {location ? <LocationBody location={location} /> : <LocationSkeleton />}
      </DrawerOrDialog.Content>
    </DrawerOrDialog.Root>
  );
};

export default LocationDetail;
